import React, { useEffect, useMemo, useState } from 'react';
import { Check, ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getImageUrl } from '@/lib/supabase';
import type { FloorplanVersion, FloorplanMessage } from '@/types';

interface FloorplanVariantPickerModalProps {
  open: boolean;
  version: FloorplanVersion | null;
  messages: FloorplanMessage[];
  /** Currently active messageNumber within the version. null = Ausgangs-Stand. */
  activeMessageNumber: number | null;
  onCancel: () => void;
  /**
   * User confirmed a variant. `path` is the storage path of the chosen image
   * (either version.initialStoragePath or a message.resultingStoragePath).
   * `messageNumber` mirrors the picker selection (null = Ausgangs-Stand).
   */
  onConfirm: (path: string, messageNumber: number | null) => void;
}

interface VariantOption {
  /** null = the version's initial generation (Ausgangs-Stand) */
  messageNumber: number | null;
  storagePath: string;
  /** Stable cache-buster — message.id or version.id */
  cacheKey: string;
  label: string;
  prompt: string | null;
}

const truncatePrompt = (prompt: string, max = 60): string => {
  const trimmed = prompt.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1).trimEnd()}…`;
};

export const FloorplanVariantPickerModal: React.FC<FloorplanVariantPickerModalProps> = ({
  open,
  version,
  messages,
  activeMessageNumber,
  onCancel,
  onConfirm,
}) => {
  const options: VariantOption[] = useMemo(() => {
    if (!version) return [];
    const initial: VariantOption = {
      messageNumber: null,
      storagePath: version.initialStoragePath,
      cacheKey: version.id,
      label: `Ausgangs-Stand (V${version.versionNumber} Initial)`,
      prompt: null,
    };
    const editOptions: VariantOption[] = messages
      .filter((m) => m.status === 'success' && !!m.resultingStoragePath)
      .map((m) => ({
        messageNumber: m.messageNumber,
        storagePath: m.resultingStoragePath as string,
        cacheKey: m.id,
        label: `Bearbeitung ${m.messageNumber}`,
        prompt: m.userPrompt,
      }));
    return [initial, ...editOptions];
  }, [version, messages]);

  // Default-selected matches the canvas-active variant. Re-sync when modal
  // opens or when the active pointer changes externally.
  const [selectedKey, setSelectedKey] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    const target = options.find((o) => o.messageNumber === activeMessageNumber) ?? options[0];
    if (target) setSelectedKey(`${target.messageNumber ?? 'init'}`);
  }, [open, activeMessageNumber, options]);

  const selected = useMemo(
    () => options.find((o) => `${o.messageNumber ?? 'init'}` === selectedKey) ?? null,
    [options, selectedKey]
  );

  const handleConfirm = () => {
    if (!selected) return;
    onConfirm(selected.storagePath, selected.messageNumber);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-left">
            Mit welchem Stand möchten Sie fortfahren?
          </DialogTitle>
          <DialogDescription className="text-left">
            Wählen Sie das Bild, das in der Branding-Ansicht als Basis verwendet wird.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 max-h-[55vh] overflow-y-auto pr-1">
          {options.map((opt) => {
            const key = `${opt.messageNumber ?? 'init'}`;
            const isSelected = key === selectedKey;
            const isCanvasActive = opt.messageNumber === activeMessageNumber;
            const thumbUrl = `${getImageUrl(opt.storagePath)}?v=${opt.cacheKey}`;

            return (
              <button
                key={key}
                type="button"
                onClick={() => setSelectedKey(key)}
                className={`flex items-start gap-3 rounded-lg border-2 p-3 text-left transition-colors ${
                  isSelected
                    ? 'border-[#fe7f2e] bg-[#fe7f2e]/5'
                    : 'border-stone-200 bg-white hover:border-stone-300'
                }`}
              >
                <div className="size-16 flex-shrink-0 overflow-hidden rounded-md border border-stone-200 bg-white">
                  <img
                    src={thumbUrl}
                    alt={opt.label}
                    className="size-full object-contain"
                    draggable={false}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-stone-800">
                      {opt.label}
                    </span>
                    {isCanvasActive && (
                      <span className="text-[11px] uppercase tracking-wide text-[#fe7f2e] font-medium">
                        aktuell aktiv
                      </span>
                    )}
                  </div>
                  {opt.prompt && (
                    <p className="mt-0.5 text-xs italic text-stone-500 leading-snug">
                      „{truncatePrompt(opt.prompt)}"
                    </p>
                  )}
                </div>

                <div
                  className={`size-5 flex-shrink-0 rounded-full border-2 flex items-center justify-center mt-1 ${
                    isSelected
                      ? 'border-[#fe7f2e] bg-[#fe7f2e] text-white'
                      : 'border-stone-300 bg-white'
                  }`}
                  aria-hidden
                >
                  {isSelected && <Check className="size-3 stroke-[3]" />}
                </div>
              </button>
            );
          })}
        </div>

        <DialogFooter className="flex-col sm:flex-row sm:justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selected}
            className="bg-[#fe7f2e] hover:bg-[#e56b1f] text-white gap-1"
          >
            Weiter mit diesem Stand
            <ArrowRight className="size-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
